import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

import CtaLink from '@/components/CtaLink';
import { withCta } from '@/lib/ctaTracking';

/**
 * HeroProductoV3 — el hero que abre las cuatro páginas de Producto.
 *
 * En el diseño hay dos variantes, y la diferencia es solo si la página tiene
 * una maqueta que enseñar:
 *
 *   - **A dos columnas** (Visibilidad SEO, Publicaciones IA): texto a la
 *     izquierda y el mock a la derecha.
 *   - **Centrado** (Automatizaciones, Widget de reseñas): sin mock, todo el
 *     bloque centrado.
 *
 * Aquí es el mismo componente y lo decide `children`: si le pasas una maqueta,
 * se pone a dos columnas; si no, se centra. Tenerlos como dos componentes
 * distintos habría duplicado los dos CTAs, el badge y los tamaños de cada
 * breakpoint, que es donde se van acumulando las diferencias que nadie quiere.
 *
 * Los dos ids de CTA son props porque lo que interesa comparar es qué página
 * de Producto trae altas — con un id fijo las cuatro se mezclarían en un solo
 * embudo. Mismo criterio que `CtaFinalOscuro`.
 */
export default function HeroProductoV3({
  badge,
  titulo,
  acento,
  tituloB,
  sub,
  cta,
  ctaVer,
  anclaVer,
  ctaTrialId,
  ctaVerId,
  children,
}) {
  const conMock = Boolean(children);

  return (
    <section className="bg-white px-6 pb-6">
      <div
        className={`max-w-[1180px] mx-auto pt-4 pb-8 ${
          conMock
            ? 'flex flex-wrap items-center gap-8 lg:gap-12'
            : 'text-center max-w-[860px]'
        }`}
      >
        <div className={conMock ? 'flex-1 basis-[420px] min-w-0' : ''}>
          {badge ? (
            <div className="inline-flex items-center bg-v3-surface border border-v3-line rounded-full px-[15px] py-2 text-[13px] font-medium text-v3-body">
              {badge}
            </div>
          ) : null}

          {/* El diseño fija 52 px. A 375 px eso deja tres palabras por línea,
              así que la escala arranca en 34 y sube por breakpoint. */}
          <h1
            className={`text-[34px] sm:text-[42px] lg:text-[52px] leading-[1.06] tracking-[-.035em] font-bold text-v3-ink ${
              badge ? 'mt-5' : ''
            } ${conMock ? 'max-w-[22ch]' : 'mx-auto max-w-[24ch]'}`}
          >
            {titulo}
            <span className="text-v3-golddeep">{acento}</span>
            {tituloB}
          </h1>

          <p
            className={`text-[17px] lg:text-[18px] leading-[1.6] text-v3-body mt-[18px] ${
              conMock ? 'max-w-[48ch]' : 'mx-auto max-w-[58ch]'
            }`}
          >
            {sub}
          </p>

          <div className={`flex gap-3 flex-wrap mt-7 ${conMock ? '' : 'justify-center'}`}>
            <CtaLink
              as={Link}
              cta={ctaTrialId}
              to={withCta('/register', ctaTrialId)}
              className="inline-flex items-center bg-v3-gold hover:bg-v3-golddeep text-v3-ink text-base font-semibold rounded-full px-[26px] py-[15px] whitespace-nowrap transition-colors"
            >
              {cta}
            </CtaLink>
            <CtaLink
              as="a"
              cta={ctaVerId}
              href={anclaVer}
              className="inline-flex items-center gap-2 bg-white border border-v3-line hover:border-v3-faint text-v3-ink text-base font-semibold rounded-full px-[25px] py-[14px] whitespace-nowrap transition-colors"
            >
              {ctaVer}
              <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </CtaLink>
          </div>
        </div>

        {conMock ? <div className="flex-1 basis-[380px] min-w-0 w-full">{children}</div> : null}
      </div>
    </section>
  );
}
